import CustomViewDialogStore from "csi/middleware/customView/CustomViewDialogStore";
import DataSheetTableOptionsStore from "csi/middleware/customView/options/DataSheetTableOptionsStore";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, computed, makeObservable } from "mobx";
import { ChangeEvent, FunctionComponent, SyntheticEvent } from "react";
import { ICustomViewTableSizeChartOptions, SizeChartDisplayOptionsType } from "src/ProductSpec/SizeChart/CustomViewSizeChartOptions";
import { ICustomViewTableSizeChartDropDownOptions, SizeChartCompareType, SizeChartDimensionFormatType, SizeChartDisplayType, SizeChartUnitsType } from "./CustomViewSizeChartOptions";

/**
 * This store contains information about Size chart options
 */
class SizeChartTableCustomViewOptionsStore extends DataSheetTableOptionsStore<ICustomViewTableSizeChartOptions> {
	/** all the dropdown boxes selection options for the custom view */
	readonly sizeChartDropDownOptions: ICustomViewTableSizeChartDropDownOptions;

	constructor(
		sizeChartDropDownOptions: ICustomViewTableSizeChartDropDownOptions,
		component: FunctionComponent,
		customViewDialogStore: CustomViewDialogStore,
		options?: ICustomViewTableSizeChartOptions,
	) {
		super(sizeChartDropDownOptions, component, customViewDialogStore, options);
		makeObservable(this);
		this.sizeChartDropDownOptions = sizeChartDropDownOptions;
	}

	@action onChangeSizeChartDisplay = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.sizeChartDisplay = value.id as SizeChartDisplayType;
	};

	@action onChangeSizeChartCompare = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.sizeChartCompare = value.id as SizeChartCompareType;
	};

	@action onChangeSizeChartDimensionFormat = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.sizeChartDimensionFormat = value.id as SizeChartDimensionFormatType;
	};

	@action onChangeSizeChartOptions = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.sizeChartDisplayAllSizes = (value.id === SizeChartDisplayOptionsType.ALL_SIZE);
		this.options.sizeChartPrintBaseSize = (value.id === SizeChartDisplayOptionsType.BASE_SIZE_ONLY);
	};

	@action onChangeSizeChartUnits = (event: ChangeEvent<{}>, value: ComponentItem): void => {
		this.options.sizeChartUnits = value.id as SizeChartUnitsType;
	};

	@action setSizeChartHighlightActualOutOfTolerance = (event: SyntheticEvent, value: boolean): void => {
		this.options.sizeChartHighlightActualOutOfTolerance = value;
	};

	@action setSizeChartHighlightBlankTolerance = (event: SyntheticEvent, value: boolean): void => {
		this.options.sizeChartHighlightBlankTolerance = value;
	};

	@action setSizeChartHighlightRevisedDeltaOutOfTolerance = (event: SyntheticEvent, value: boolean): void => {
		this.options.sizeChartHighlightRevisedDeltaOutOfTolerance = value;
	};

	@action setSizeChartHighlightBlank = (event: SyntheticEvent, value: boolean): void => {
		this.options.sizeChartHighlightBlank = value;
	};

	@action setSizeChartHighlightDeltaOutOfTolerance = (event: SyntheticEvent, value: boolean): void => {
		this.options.sizeChartHighlightDeltaOutOfTolerance = value;
	};	

	@computed get selectedSizeChartDimensionFormat(): ComponentItem {
		const { sizeChartDimensionFormat } = this.options;
		return this.sizeChartDropDownOptions.sizeChartDimensionFormatOptions.find((item) => item.id === sizeChartDimensionFormat);
	};

	@computed get selectedSizeChartOption(): ComponentItem {
		const { sizeChartDisplayAllSizes, sizeChartPrintBaseSize } = this.options;
		let selectedOption: string;

		if (sizeChartDisplayAllSizes) {
			selectedOption = SizeChartDisplayOptionsType.ALL_SIZE;
		} else if (sizeChartPrintBaseSize) {
			selectedOption = SizeChartDisplayOptionsType.BASE_SIZE_ONLY;
		} else if (sizeChartDisplayAllSizes === false && sizeChartPrintBaseSize === false) {
			selectedOption = SizeChartDisplayOptionsType.SELECTED_SIZES;
		}

		return this.sizeChartDropDownOptions.sizeChartDisplayOptions.find((item) => item.id === selectedOption);
	};

	@computed get selectedSizeChartUnits(): ComponentItem {
		const { sizeChartUnits } = this.options;
		return this.sizeChartDropDownOptions.sizeChartUnitsOptions.find((item) => item.id === sizeChartUnits);
	};

	@computed get selectedSizeChartIncrementDisplay(): ComponentItem {
		const { sizeChartDisplay } = this.options;
		return this.sizeChartDropDownOptions.sizeChartIncrementDisplayOptions.find((item) => item.id === sizeChartDisplay);
	};

	@computed get selectedSizeChartCompare(): ComponentItem {
		const { sizeChartCompare } = this.options;
		return this.sizeChartDropDownOptions.sizeChartCompareOptions.find((item) => item.id === sizeChartCompare);
	};
}

export = SizeChartTableCustomViewOptionsStore;